import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { onBillingUsageChanged, onPlanChanged } from '@/lib/billingEvents';
import {
  canProcessPendingApplications,
  reconcileDeferredCountsWithApplications,
} from '@/lib/pendingProcessing';
import { patchUnlockedDeferredApplications } from '@/lib/recruiterCache';
import { usePendingBillingStore } from '@/stores/pendingBillingStore';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useApplicationsQuery } from '@/hooks/useRecruiterListQueries';

/**
 * Syncs persisted deferred-application counts from the BE on every page.
 * Calls unlock-deferred first so PENDING_UPGRADE → READY_TO_PROCESS is
 * written to Firestore when the plan has capacity.
 */
export function useSyncPendingBillingCount() {
  const { selectedCompany } = useAuth();
  const queryClient = useQueryClient();
  const setDeferredCounts = usePendingBillingStore(s => s.setDeferredCounts);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const applicationsQuery = useApplicationsQuery(selectedCompany?.id, false);

  const applyCounts = useCallback(
    (api: { pendingUpgrade: number; readyToProcess: number; billingRetry: number }) => {
      if (!selectedCompany) return;
      const merged = reconcileDeferredCountsWithApplications(
        queryClient,
        selectedCompany.id,
        hasProcessingCredits,
        api,
      );
      setDeferredCounts(merged);
    },
    [selectedCompany, queryClient, hasProcessingCredits, setDeferredCounts],
  );

  const sync = useCallback(async () => {
    if (!selectedCompany) {
      setDeferredCounts({ pendingUpgrade: 0, readyToProcess: 0, billingRetry: 0 });
      return;
    }
    const companyId = selectedCompany.id;
    try {
      const result = await api.applications.unlockDeferred(companyId);
      if (result.unlocked > 0) {
        patchUnlockedDeferredApplications(queryClient, companyId);
      }
      applyCounts({
        pendingUpgrade: result.pendingUpgrade,
        readyToProcess: result.readyToProcess,
        billingRetry: result.billingRetry,
      });
      await queryClient.invalidateQueries({ queryKey: ['applications', companyId] });
    } catch {
      // Keep the last known counts on transient fetch errors.
    }
  }, [selectedCompany, setDeferredCounts, queryClient, applyCounts]);

  useEffect(() => {
    if (!selectedCompany || !applicationsQuery.data?.length) return;
    const current = usePendingBillingStore.getState();
    applyCounts({
      pendingUpgrade: current.pendingUpgrade,
      readyToProcess: current.readyToProcess,
      billingRetry: current.billingRetry,
    });
  }, [selectedCompany, applicationsQuery.data, hasProcessingCredits, applyCounts]);

  useEffect(() => {
    void sync();
  }, [sync]);

  useEffect(() => {
    return onPlanChanged(() => {
      void sync();
    });
  }, [sync]);

  useEffect(() => {
    return onBillingUsageChanged(() => {
      void sync();
    });
  }, [sync]);
}
